import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDollar, faUtensils } from '@fortawesome/pro-duotone-svg-icons';
import { faKitchenSet } from '@fortawesome/pro-solid-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import AllergenList from 'classes/MenuPlan/Detail/AllergenList';
import Column from 'classes/MenuPlan/Detail/Column';
import Item from 'classes/MenuPlan/Detail/Item';
import Menu from 'classes/MenuPlan/Detail/Menu';
import MenuPlan from 'classes/MenuPlan/Detail/MenuPlan';
import Recipe from 'classes/MenuPlan/Detail/Recipe';
import Row from 'classes/MenuPlan/Detail/Row';
import TagList from 'classes/MenuPlan/Detail/TagList';
import 'components/desktop/MenuPlan/Detail/ItemComponent.scss';
import ItemInputField from 'components/desktop/MenuPlan/Detail/ItemInputField';
import AddToMenuOrRecipe from 'components/desktop/_general/AddMenuOrRecipe/AddMenuOrRecipe';
import IconComponent from 'components/desktop/_general/Icon/IconComponent';
import ImageSlider from 'components/desktop/_general/ImageSlider/ImageSlider';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Modal from 'components/desktop/_general/Modal/Modal';
import ENTITLEMENTS from 'enums/entitlements';
import clone from 'functions/clone';
import { getNumberString, getPriceString } from 'functions/numberToString';
import { areEntitlementsInUserEntitlements } from 'functions/tokenFunctions';
import { usePermissions } from 'hooks/usePermissions';
import { RootState } from 'reducers/rootReducer';
import { AllergenLight } from 'types/Allergen/AllergenLight';
import { TagLight } from 'types/Tag/TagLight';

interface IProps {
	id?: string;
	item?: Item;
	day: number;
	rowIndex: number;
	menuPlan: MenuPlan;
	setMenuPlan: (input: MenuPlan) => void;
	row: Row;
	rowHeight: number;
	rowItemWidth: number;
	getSelectedItemFromApi: (input: Item) => Promise<void>;
	selectedItem: Recipe | Menu | Row | Item | Column | null;
	setSelectedItem: (input: Row | Item | null) => void;
	handleRemoveItem: (day: number, rowId: string) => void;
}

function urlHasCopy(): boolean {
	return window.location.href.toLowerCase().includes('copy');
}

const ItemComponent = (props: IProps) => {
	const navigate = useNavigate();
	const { isMenuPlanEditor } = usePermissions();
	const { isRecipeReader } = usePermissions();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const reduxCurrency: string = useSelector((state: RootState) => state.currency);
	const [showButtons, setShowButtons] = useState<boolean>(false);
	const [addItemModalVisible, setAddItemModalVisible] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [modalMenuPlanId, setModalMenuPlanId] = useState<string>(props.menuPlan.id);
	const isDisabled = props.menuPlan.isDisabled();

	const [justFilled, setJustFilled] = useState(false);
	const [scanAnim, setScanAnim] = useState(false);
	const prevItemRef = useRef(props.item);

	useEffect(() => {
		if (!prevItemRef.current && props.item) {
			setJustFilled(true);
			setScanAnim(false);

			// trigger scan after grow finishes
			setTimeout(() => {
				setJustFilled(false);
				setScanAnim(true);

				// remove scan class after animation completes
				setTimeout(() => setScanAnim(false), 1000);
			}, 600); // match grow duration
		}
		prevItemRef.current = props.item;
	}, [props.item]);

	const handleRemoveItem = (): void => {
		const day: number | undefined = props.item?.day;
		if (day !== undefined) {
			props.handleRemoveItem(day, props.row.id);
			setShowButtons(false);
		}
	};

	const handleSelectItem = (): void => {
		if (isRecipeReader) {
			if (props.item) {
				props.getSelectedItemFromApi(props.item);
			}
		}
	};

	const addItemToExistingMenuPlan = async (): Promise<void> => {
		setIsLoading(true);

		const response = await props.menuPlan.callApiPut(reduxCurrency);

		let error = true;

		response.do((x) => {
			if (x.id ?? props.menuPlan.id) {
				setModalMenuPlanId(x.id ?? props.menuPlan.id);
				setAddItemModalVisible(true);
				error = false;
			}
		});

		if (error) {
			setIsLoading(false);
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: 'auto',
			});
		}

		setIsLoading(false);
	};

	const handleAddItem = async (): Promise<void> => {
		if (!isMenuPlanEditor) return;
		if (modalMenuPlanId) {
			addItemToExistingMenuPlan();
		}
	};

	const handleAddedItem = async (): Promise<void> => {
		setIsLoading(true);
		navigate(`/menuPlan/detail/${modalMenuPlanId}`);
		await props.menuPlan.callApiGet(modalMenuPlanId, reduxCurrency);
		await props.menuPlan.recalculateColumnsAndRows(reduxCurrency);
		props.setMenuPlan(clone(props.menuPlan));
		setIsLoading(false);
	};

	const onMouseOver = (): void => {
		if (isMenuPlanEditor) setShowButtons(true);
	};

	const getRowDayName = (): string => {
		const startDate = new Date(props.menuPlan.startDate);
		const currentDate = new Date(startDate);
		currentDate.setDate(startDate.getDate() + props.day);

		const dateString = currentDate.toLocaleDateString(reduxCultureCode, {
			weekday: 'long',
			day: '2-digit',
			month: '2-digit',
		});

		const rowName = props.menuPlan.rowList.all[props.rowIndex]?.name?.[reduxCultureCode] ?? '';
		return `${dateString} – ${rowName}`;
	};

	const getIcons = (input: Item): Array<AllergenLight | TagLight> => {
		const output: Array<AllergenLight | TagLight> = [];

		const tags: TagList = input.tags;
		for (const tag of tags.saved) {
			if (tag.iconSvgUrl && tag.display) {
				output.push({
					id: tag.id,
					display: tag.display,
					iconSvgUrl: tag.iconSvgUrl,
					name: tag.name ?? ' ',
					tagCategory: {
						id: tag.tagCategory.id,
						name: tag.tagCategory.name ?? ' ',
					},
				});
			}
		}

		const allergens: AllergenList = input.allergens;
		for (const allergen of allergens.saved) {
			if (allergen.iconSvgUrl && allergen.display) {
				output.push({
					id: allergen.id,
					display: allergen.display,
					iconSvgUrl: allergen.iconSvgUrl,
					logic: allergen.logic,
					name: allergen.name ?? '',
				});
			}
		}
		return output;
	};

	const onMouseLeave = (): void => {
		setShowButtons(false);
	};
	if (props.item) {
		return (
			<div
				className={`item-appear-grow ${justFilled ? 'animate' : ''} ${
					scanAnim ? 'scan-animate' : ''
				}`}
				style={{
					height: props.rowHeight + 'px',
					width: props.rowItemWidth + 'px',
				}}
			>
				<div
					onClick={() => handleSelectItem()}
					onMouseOver={onMouseOver}
					onMouseLeave={onMouseLeave}
					className="item-component-recipe"
					style={{
						height: props.rowHeight + 'px',
						width: props.rowItemWidth + 'px',
						border:
							props.selectedItem?.id === props.item?.id
								? '3px solid #6699cc'
								: undefined,
					}}
				>
					<LoadingAnimation isLoading={isLoading}></LoadingAnimation>
					{props.item.images.all.length > 0 ? (
						<ImageSlider
						images={props.item.images.all}
						id={props.item.id}
						children={props.item.children}
					/>
					) : (
						<div
							className="img-placeholder"
							style={{
								width: 'auto',
							}}
						>
							<FontAwesomeIcon icon={faKitchenSet as IconProp} className="fa-5x" />
						</div>
					)}
					<div style={{ margin: '10px' }}>
						<div className="recipe-informations" title={props.item.title?.[reduxCultureCode]}>
							<strong>{props.item.title?.[reduxCultureCode]}</strong>
						</div>
						<hr style={{ marginTop: '8px', marginBottom: '5px' }} />
						{t('measurments:PER_PART')}
						<div>
							<FontAwesomeIcon
								icon={faDollar as IconProp}
								className="fa-1x"
								style={{ marginRight: '11px' }}
							/>
							{props.item.price ? (
								<>
									<b>{getPriceString(props.item.price, reduxCultureCode)} </b>
									{reduxCurrency}
								</>
							) : (
								<b>–</b>
							)}
							<br />
							<FontAwesomeIcon
								icon={faUtensils as IconProp}
								className="fa-1x"
								style={{ marginRight: '7px' }}
							/>
							{props.item.calories ? (
								<>
									<b>
										{getNumberString(
											props.item.calories,
											reduxCultureCode,
											true
										)}
									</b>
									{' ' + t('_general:CALORIES')}
								</>
							) : (
								<b>–</b>
							)}
						</div>
						<hr style={{ marginTop: '8px', marginBottom: '8px' }} />
						<div>
							{
								<>
									<div className="d-flex flex-gap-10">
										{getIcons(props.item).map(
											(icon: AllergenLight | TagLight, i: number) => (
												<IconComponent height='2vh' width='2vh' icon={icon} key={i} />
											)
										)}
									</div>
								</>
							}
						</div>
					</div>
					<div style={{ position: 'absolute', bottom: 0, margin: '10px' }}>
						<ItemInputField
							item={props.item}
							menuPlan={props.menuPlan}
							setMenuPlan={props.setMenuPlan}
						/>
					</div>
					<div
						className="recipe-buttons"
						style={{
							opacity: showButtons ? '1' : '0',
						}}
					>
						<div className="text-center" style={{ margin: '5px' }}>
							<button
								type="button"
								disabled={isDisabled || urlHasCopy()}
								className="btn btn-outline-primary"
								onClick={handleRemoveItem}
							>
								<FontAwesomeIcon icon={faTrash as IconProp} />
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	} else {
		if (isMenuPlanEditor && !isDisabled && !urlHasCopy()) {
			return (
				<div
					className="item-component-buttons"
					style={{
						height: props.rowHeight + 'px',
						width: props.rowItemWidth + 'px',
					}}
				>
					<LoadingAnimation isLoading={isLoading}></LoadingAnimation>
					<Modal
						headerText={getRowDayName()}
						bodyContent={
							<AddToMenuOrRecipe
								sourceItemId={modalMenuPlanId}
								colNumber={props.day}
								rowNumber={props.rowIndex}
								sourceItemType="menuPlan"
								finalizeAdding={() => {
									setAddItemModalVisible(false);
									handleAddedItem();
								}}
							></AddToMenuOrRecipe>
						}
						isOpen={addItemModalVisible}
						headerIcon={<FontAwesomeIcon icon={faPlus}></FontAwesomeIcon>}
						close={() => {
							setAddItemModalVisible(false);
							navigate(`/menuPlan/detail/${modalMenuPlanId}`);
						}}
						wide={true}
					></Modal>

					<div
						className="button top"
						style={{
							height: areEntitlementsInUserEntitlements([ENTITLEMENTS.MENU])
								? '50%'
								: '100%',
						}}
						onClick={() => handleAddItem()}
					>
						<FontAwesomeIcon
							icon={faPlus as IconProp}
							className="fa-2x"
							style={{ marginRight: '8px' }}
						/>
						{t('_general:RECIPE')}/{t('_general:MENU')}
					</div>
				</div>
			);
		}
		return (
			<div
				className="item-component-buttons"
				style={{
					height: props.rowHeight + 'px',
					width: props.rowItemWidth + 'px',
				}}
			></div>
		);
	}
};

export default ItemComponent;
